import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { randToCents } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function TenantNew() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    slug: "", business_name: "", trading_name: "",
    plan: "management_only", billing_cycle: "monthly",
    price_rand: "", discount_rand: "", status: "trial",
    owner_email: "", owner_username: "", owner_password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const bind = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = {
        slug: form.slug.trim().toLowerCase(),
        business_name: form.business_name.trim(),
        trading_name: form.trading_name.trim() || null,
        plan: form.plan,
        billing_cycle: form.billing_cycle,
        price_cents: randToCents(form.price_rand),
        discount_cents: randToCents(form.discount_rand),
        status: form.status,
        owner_email: form.owner_email.trim().toLowerCase(),
        owner_username: form.owner_username.trim() || null,
        owner_password: form.owner_password,
      };
      const { data } = await api.post("/platform/tenants", body);
      toast.success(`Tenant "${data.business_name}" created`);
      nav(`/platform/tenants/${data.id}`);
    } catch (err) {
      toast.error(formatError(err, "Failed to create tenant"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <div className="label-overline">Platform · Tenants</div>
        <h1 className="text-3xl font-black tracking-tight mt-1 text-slate-900">New Truck Stop</h1>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded p-6 space-y-5">
        <div className="label-overline text-slate-800">Business</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="label-overline">Slug</Label>
            <Input data-testid="tn-slug" className="rounded h-10 mt-1 font-mono"
                   required pattern="^[a-z0-9-]{3,40}$"
                   placeholder="e.g. n1-truckstop"
                   value={form.slug} onChange={bind("slug")} />
          </div>
          <div>
            <Label className="label-overline">Business name</Label>
            <Input data-testid="tn-business-name" className="rounded h-10 mt-1"
                   required value={form.business_name} onChange={bind("business_name")} />
          </div>
          <div>
            <Label className="label-overline">Trading name</Label>
            <Input data-testid="tn-trading" className="rounded h-10 mt-1"
                   value={form.trading_name} onChange={bind("trading_name")} />
          </div>
        </div>

        <div className="label-overline text-slate-800 pt-2">Plan & Billing</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="label-overline">Plan</Label>
            <select data-testid="tn-plan" className="w-full mt-1 h-10 border border-slate-300 rounded px-2 font-mono"
                    value={form.plan} onChange={bind("plan")}>
              <option value="management_only">management_only</option>
              <option value="financial">financial</option>
            </select>
            {form.plan === "financial" && (
              <div className="text-[11px] text-blue-700 mt-1 leading-snug"
                   data-testid="tn-plan-financial-hint">
                Enables customer balances, invoices, payments, statements,
                credit notes, and credit limits.
              </div>
            )}
          </div>
          <div>
            <Label className="label-overline">Cycle</Label>
            <select data-testid="tn-cycle" className="w-full mt-1 h-10 border border-slate-300 rounded px-2 font-mono"
                    value={form.billing_cycle} onChange={bind("billing_cycle")}>
              <option value="monthly">monthly</option>
              <option value="6_months">6_months</option>
              <option value="12_months">12_months</option>
            </select>
          </div>
          <div>
            <Label className="label-overline">Price (R)</Label>
            <Input data-testid="tn-price" className="rounded h-10 mt-1 font-mono"
                   type="text" inputMode="decimal" placeholder="e.g. 499.00"
                   value={form.price_rand} onChange={bind("price_rand")} />
          </div>
          <div>
            <Label className="label-overline">Discount (R)</Label>
            <Input data-testid="tn-discount" className="rounded h-10 mt-1 font-mono"
                   type="text" inputMode="decimal" placeholder="0.00"
                   value={form.discount_rand} onChange={bind("discount_rand")} />
          </div>
        </div>
        <div>
          <Label className="label-overline">Initial status</Label>
          <select data-testid="tn-status" className="w-64 mt-1 h-10 border border-slate-300 rounded px-2 font-mono"
                  value={form.status} onChange={bind("status")}>
            <option value="trial">trial</option>
            <option value="active">active</option>
          </select>
        </div>

        <div className="label-overline text-slate-800 pt-2">First Owner user</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="label-overline">Owner email</Label>
            <Input data-testid="tn-owner-email" className="rounded h-10 mt-1 font-mono"
                   type="email" required value={form.owner_email} onChange={bind("owner_email")} />
          </div>
          <div>
            <Label className="label-overline">Username</Label>
            <Input data-testid="tn-owner-username" className="rounded h-10 mt-1 font-mono"
                   value={form.owner_username} onChange={bind("owner_username")} />
          </div>
          <div>
            <Label className="label-overline">Initial password (min 8)</Label>
            <Input data-testid="tn-owner-password" className="rounded h-10 mt-1 font-mono"
                   type="text" required minLength={8} value={form.owner_password} onChange={bind("owner_password")} />
          </div>
        </div>

        <div className="pt-4 flex flex-col-reverse sm:flex-row justify-end gap-3 border-t border-slate-200">
          <Button type="button" variant="outline" className="rounded"
                  onClick={() => nav("/platform/tenants")}>Cancel</Button>
          <Button type="submit" data-testid="tn-submit" disabled={submitting}
                  className="rounded bg-blue-700 hover:bg-blue-800">
            {submitting ? "Creating…" : "Create tenant & owner"}
          </Button>
        </div>
      </form>
    </div>
  );
}
